import type { Feature, LineString } from "geojson";
import type { MapObjectProperties } from "@iventis/map-types";
import type { BehaviorSubject } from "rxjs";
import type { ExpressionSpecification, LineLayerSpecification, Map } from "@iventis/mapbox-gl";
import { createStaticStyleValue, getStaticStyleValue } from "@iventis/layer-style-helpers";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { LineModelStyle } from "@iventis/domain-model/model/lineModelStyle";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { LineType } from "@iventis/domain-model/model/lineType";
import { StylePropertyToValueMapV2 } from "../../types/style-types";
import { MapState } from "../../types/store-schema";
import { LineModelGuideLineLayer } from "../generic-layers/line-model-guide-line-layer";
import { iventisLineToAggregateLayer, styleValueParser, styleValueToMapboxStyleValue } from "../mapbox/engine-mapbox-helpers";
import { MapboxLocalLayer } from "./mapbox-local-layer";
import { updateLayerFilters } from "../mapbox/filters/engine-mapbox-filter-helpers";
import { FilterType } from "../mapbox/filters/engine-mapbox-filter-types";
import { createMapboxLevelFilter } from "../mapbox/filters/engine-mapbox-level-filter-helpers";
import { SubLayerType } from "../mapbox/sublayer-types";

export class MapboxLineModelGuideLineLayer extends LineModelGuideLineLayer {
    private readonly map: Map;

    private readonly lineModelId: string;

    private readonly mapboxLayer: MapboxLocalLayer<Feature<LineString, MapObjectProperties>[], LineLayerSpecification>;

    constructor(state$: BehaviorSubject<MapState>, lineModelLayerId: string, map: Map) {
        super(state$, lineModelLayerId);
        this.map = map;
        this.lineModelId = lineModelLayerId;
        this.mapboxLayer = new MapboxLocalLayer(map, this.layerId, this.sourceId);

        this.addLocalSource();
        this.addLayer();
        const features = this.getGeoJson();
        if (features != null) {
            this.updateLocalSource(features as Feature<LineString, MapObjectProperties>[]);
        }
    }

    public showGuideLinesFor(objectIds: string[]) {
        if (this.map.getLayer(this.layerId)) {
            const newFilter = this.createMapObjectFilter(objectIds);
            this.mapboxLayer.updateLayerFilter(newFilter);
        }
    }

    public showAllGuideLines() {
        if (this.map.getLayer(this.layerId)) {
            this.mapboxLayer.removeLayerFilter(FilterType.LineModelGuideLine);
        }
    }

    public removeGuideLines() {
        if (this.map.getLayer(this.layerId)) {
            this.mapboxLayer.updateLayerFilter(this.createMapObjectFilter());
        }
    }

    public updateMapOrder() {
        this.mapboxLayer.updateMapOrder(this.lineModelId);
    }

    protected addLayer(): void {
        const iventisLayer = this.getIventisLayer();
        const styleValue = iventisLayer.lineModelStyle?.lineModelShowLine;
        const alwaysShowGuideLines = styleValue != null ? getStaticStyleValue(styleValue) : false;
        const filter = alwaysShowGuideLines ? null : updateLayerFilters(null, this.createMapObjectFilter());

        const layers = iventisLineToAggregateLayer(this.guideLineLayer, false, { projectDataFields: [], unitOfMeasurement: UnitOfMeasurement.Metric }, filter);

        layers.forEach((aggregateLayer) => {
            const { layer } = aggregateLayer;
            if (aggregateLayer.type !== SubLayerType.LINE_OUTLINE && layer.type === "line") {
                const outline = getStaticStyleValue(this.guideLineLayer.lineStyle.outline) ?? false;
                if (outline) {
                    layer.paint = {
                        ...layer.paint,
                        "line-border-color": styleValueToMapboxStyleValue(this.guideLineLayer.lineStyle.outlineColour),
                        "line-border-width": styleValueToMapboxStyleValue(this.guideLineLayer.lineStyle.outlineWidth),
                    };
                }

                // eslint-disable-next-line no-param-reassign
                layer.metadata = {
                    name: iventisLayer.name,
                    type: "guide-line",
                };
                this.mapboxLayer.addLayer(layer, this.lineModelId);
            }
        });
        this.updateMapLevel(this.state$.value.currentLevel);
        const dateFilter = this.state$.value.datesFilter.value;
        if (dateFilter.filter) {
            this.addDateFilter();
        }
    }

    public removeLayer(): void {
        this.mapboxLayer.removeLayer();
    }

    protected addLocalSource(): void {
        this.mapboxLayer.addSource();
    }

    public updateLocalSource(source: Feature<LineString, MapObjectProperties>[]): void {
        this.mapboxLayer.updateSource(source);
    }

    public removeLocalSource(): void {
        this.mapboxLayer.removeSource();
    }

    public updateStyle(styleChanges: StylePropertyToValueMapV2<LineModelStyle>): void {
        // Update show line under models if changed
        if (styleChanges.lineModelShowLine) {
            const showAll = getStaticStyleValue(styleChanges.lineModelShowLine);
            if (showAll) {
                // Style the guide lines according to the line model style
                this.showAllGuideLines();
                const style = this.getIventisLayer().lineModelStyle;
                this.map.setPaintProperty(this.layerId, "line-color", styleValueToMapboxStyleValue(style.lineModelLineColour));
                this.map.setPaintProperty(this.layerId, "line-width", styleValueToMapboxStyleValue(style.lineModelLineWidth));
                this.map.setPaintProperty(this.layerId, "line-dasharray", styleValueParser.lineType(style.lineModelLineType));
                this.map.setPaintProperty(this.layerId, "line-border-width", styleValueToMapboxStyleValue(style.lineModelLineOutlineWidth));
                this.map.setPaintProperty(this.layerId, "line-border-color", styleValueToMapboxStyleValue(style.lineModelLineOutlineColour));
            } else {
                // Revert to use the default guide line style on selection
                const selectedMapObjects = this.getSelectedMapObjects();
                this.showGuideLinesFor(selectedMapObjects.map((obj) => obj.objectId));
                this.map.setPaintProperty(this.layerId, "line-color", "black");
                this.map.setPaintProperty(this.layerId, "line-width", 5);
                this.map.setPaintProperty(this.layerId, "line-dasharray", styleValueParser.lineType(createStaticStyleValue<LineType>(LineType.Solid)));
                this.map.setPaintProperty(this.layerId, "line-border-width", 0);
                this.map.setPaintProperty(this.layerId, "line-border-color", "#000000");
            }
        }

        // Update colour if changed
        if (styleChanges.lineModelLineColour) {
            this.map.setPaintProperty(this.layerId, "line-color", styleValueToMapboxStyleValue(styleChanges.lineModelLineColour));
        }
        // Update width if changed
        if (styleChanges.lineModelLineWidth) {
            this.map.setPaintProperty(this.layerId, "line-width", styleValueToMapboxStyleValue(styleChanges.lineModelLineWidth));
        }
        // Update type if changed
        if (styleChanges.lineModelLineType) {
            this.map.setPaintProperty(this.layerId, "line-dasharray", styleValueParser.lineType(styleChanges.lineModelLineType));
        }
        // Update show outline if changed
        if (styleChanges.lineModelLineOutline) {
            const style = this.getIventisLayer().lineModelStyle;
            const outlineWidth = getStaticStyleValue(styleChanges.lineModelLineOutline) ? styleValueToMapboxStyleValue(style.lineModelLineOutlineWidth) : 0;
            this.map.setPaintProperty(this.layerId, "line-border-width", outlineWidth);
        }
        // Update outline colour if changed
        if (styleChanges.lineModelLineOutlineColour) {
            this.map.setPaintProperty(this.layerId, "line-border-color", styleValueToMapboxStyleValue(styleChanges.lineModelLineOutlineColour));
        }
        // Update outline width if changed
        if (styleChanges.lineModelLineOutlineWidth) {
            const style = this.getIventisLayer().lineModelStyle;
            // Need to check here as if outline is disabled within the same update, we don't want to turn it back on
            if (getStaticStyleValue(style.lineModelLineOutline)) {
                this.map.setPaintProperty(this.layerId, "line-border-width", styleValueToMapboxStyleValue(styleChanges.lineModelLineOutlineWidth));
            }
        }
    }

    public updateMapLevel(level: number): void {
        const levelFilter = createMapboxLevelFilter(level);
        this.mapboxLayer.updateLayerFilter(levelFilter);
    }

    public hideLayer(): void {
        this.mapboxLayer.hideLayer();
    }

    public showLayer(): void {
        this.mapboxLayer.showLayer();
    }

    public getLayerIdsForSnapping(): string[] {
        return [this.layerId];
    }

    private createMapObjectFilter(objectIds: string[] = []): ExpressionSpecification {
        return ["in", ["get", "id"], ["literal", objectIds]];
    }

    public addDateFilter() {
        if (this.getIventisLayer().dataFields.some((df) => df.type === DataFieldType.RepeatedTimeRanges)) {
            this.mapboxLayer.addDateFilter();
        }
    }

    public removeDateFilter() {
        this.mapboxLayer.removeDateFilter();
    }
}
